import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LLMProvider, LLMModel, Conversation, Message } from '../types';

interface ChatState {
  // LLM Settings
  selectedLLM: LLMProvider;
  selectedModel: string;
  availableModels: LLMModel[];
  
  // Conversations
  conversations: Conversation[];
  activeConversationId: string | null;
  
  // UI State
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setSelectedLLM: (provider: LLMProvider, model: string) => void;
  setAvailableModels: (models: LLMModel[]) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  deleteConversation: (id: string) => void;
  setActiveConversation: (id: string | null) => void;
  addMessage: (conversationId: string, message: Message) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearConversations: () => void;
  loadConversations: (conversations: Conversation[]) => void;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      // Initial state
      selectedLLM: 'yandex',
      selectedModel: 'yandexgpt',
      availableModels: [],
      conversations: [],
      activeConversationId: null,
      isLoading: false,
      error: null,
      
      // Actions
      setSelectedLLM: (provider, model) => 
        set({ selectedLLM: provider, selectedModel: model }),
      
      setAvailableModels: (models) => 
        set({ availableModels: models }),
      
      addConversation: (conversation) => 
        set((state) => ({
          conversations: [conversation, ...state.conversations],
          activeConversationId: conversation.id,
        })),
      
      updateConversation: (id, updates) =>
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === id ? { ...conv, ...updates } : conv
          ),
        })),
      
      deleteConversation: (id) =>
        set((state) => ({
          conversations: state.conversations.filter((conv) => conv.id !== id),
          activeConversationId: 
            state.activeConversationId === id ? null : state.activeConversationId,
        })),
      
      setActiveConversation: (id) => 
        set({ activeConversationId: id }),
      
      addMessage: (conversationId, message) =>
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  messages: [...conv.messages, message],
                  messageCount: conv.messageCount + 1,
                  updatedAt: new Date().toISOString(),
                }
              : conv
          ),
        })),
      
      setLoading: (loading) => 
        set({ isLoading: loading }),
      
      setError: (error) => 
        set({ error }),
      
      clearError: () => 
        set({ error: null }),
      
      clearConversations: () =>
        set({ conversations: [], activeConversationId: null }),
      
      loadConversations: (conversations) =>
        set({ conversations }),
    }),
    {
      name: 'chat-store',
      partialize: (state) => ({
        selectedLLM: state.selectedLLM,
        selectedModel: state.selectedModel,
        // НЕ сохраняем conversations в localStorage - загружаем из БД
      }),
    }
  )
);

